import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styled-components';
import { animateScroll as scroll } from 'react-scroll';

import { State } from 'src/state/ducks';
import * as bakedBreadActions from 'src/state/ducks/bakedBread/actions';
import * as bakedBreadTagActions from 'src/state/ducks/bakedBreadTag/actions';
import * as notificationActions from 'src/state/ducks/notification/actions';
import * as notificationTemplateActions from 'src/state/ducks/notificationTemplate/actions';
import * as shopActions from 'src/state/ducks/shop/actions';
import { NotificationEntity } from 'src/firebase/db/Notifications';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';
import { toaster } from 'src/helpers/UtilityHelper';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import NotificationCreateForm from 'src/components/organisms/form/NotificationCreateForm';
import NotificationConfirmForm from 'src/components/organisms/form/NotificationConfirmForm';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';

type OwnProps = {
  notification_template_id: string | undefined;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const NotificationCreatePage = (props: Props) => {
  const [isBacked, setIsBacked] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState('');

  const {
    notification_template_id,
    login_shop_id,
    getShop,
    getBakedBreads,
    getBakedBreadTags,
    getNotificationTemplate,
    notification,
    notificationTemplate,
    bakedBreads,
    bakedBreadTags,
    shop,
    isFetching,
    isFetchedNotificationTemplate,
    isValidated,
    initNotification,
    initNotificationTemplate,
    validate,
    edit,
    create,
    errors,
    history,
  } = props;

  // 確認画面から戻った直後、ページ最上部にスクロールさせる
  useEffect(() => {
    if (!isValidated || errors.length) {
      scroll.scrollToTop();
    } else {
      window.scrollTo(0, 0);
    }
  }, [errors, isValidated]);

  // データ読み込み、離脱時に初期化
  useEffect(() => {
    if (notification_template_id === 'undefined') {
      history.push('/notification_templates');
      toaster('warning', 'まだデータの準備中です。しばらくお待ちください。');
      return;
    }

    const init = () => {
      initNotification();
      initNotificationTemplate();
    };

    const fetch = async () => {
      await init();
      if (notification_template_id) {
        await getNotificationTemplate(notification_template_id);
      }
      await getShop(login_shop_id);
      await getBakedBreads(login_shop_id);
      await getBakedBreadTags();
    };
    fetch();

    return () => init();
  }, [
    login_shop_id,
    notification_template_id,
    getBakedBreads,
    getBakedBreadTags,
    getNotificationTemplate,
    getShop,
    initNotification,
    initNotificationTemplate,
    history,
  ]);

  // 確認画面から戻る
  const onEdit = useCallback(
    (payload: NotificationEntity) => {
      if (notification_template_id) {
        setIsBacked(true);
      }
      edit(notification);
    },
    [edit, notification, notification_template_id, setIsBacked],
  );

  // 保存処理を実行
  const onCreate = useCallback(
    (payload: NotificationEntity) => {
      create(payload);
    },
    [create],
  );

  // 確認画面に遷移する前にバリデーション実行
  const onValidate = useCallback(
    (payload: NotificationEntity) => {
      validate(payload);
    },
    [validate],
  );

  const onChangeTagFilter = useCallback((bakedBreadTagId: string) => {
    setSelectedTagId(bakedBreadTagId);
  }, []);

  return (
    <DefaultLayout {...props}>
      <Container>
        <SpinnerModal isLoading={isFetching} />

        {isValidated ? (
          <>
            <Heading tag="h1" text="お知らせ内容のご確認" />
            <NotificationConfirmForm
              notification={notification}
              onBack={onEdit}
              onSubmit={onCreate}
              isFetching={isFetching}
            />
          </>
        ) : (
          <>
            <Heading tag="h1" text="パンの焼き上がりをお知らせします" />
            <NotificationCreateForm
              notification={notification}
              bakedBreads={bakedBreads}
              bakedBreadTags={bakedBreadTags}
              shop={shop}
              onSubmit={onValidate}
              onChangeTag={onChangeTagFilter}
              selectedTagId={selectedTagId}
              errors={errors}
              notificationTemplateId={notification_template_id}
              notificationTemplate={notificationTemplate}
              isFetchedNotificationTemplate={isFetchedNotificationTemplate}
              isBacked={isBacked}
            />
          </>
        )}
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M16};
  .order__annotaion {
    font-size: 12px;
    color: ${StyleConfig.COLOR.SECONDORY};
    &.bold {
      font-size: 14px;
      font-weight: bold;
      padding: ${StyleConfig.HELPER.M16};
    }
  }
`;

const mapStateToProps = (state: State) => {
  const {
    notification: { draft: notification, isValidated },
    notificationTemplate: { template: notificationTemplate },
    bakedBread: { resource: bakedBreads },
    shop: {
      currentShop: { resource: shop },
    },
    bakedBreadTag: { resource: bakedBreadTags },
  } = state;

  return {
    notification,
    notificationTemplate,
    isFetchedNotificationTemplate: state.notificationTemplate.isFetched,
    bakedBreads: bakedBreads.filter((v) => !v.is_stopped),
    bakedBreadTags: bakedBreadTags.filter(
      ({ is_disabled, is_featured }) => !is_disabled && is_featured,
    ),
    shop,
    isFetching:
      state.bakedBread.isFetching ||
      state.shop.isFetching ||
      state.notification.isFetching ||
      state.notificationTemplate.isFetching,
    isValidated,
    errors: state.notification.validationErrors,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getShop: (shop_id: string) => dispatch(shopActions.getShop(shop_id)),
  getNotificationTemplate: (notification_template_id: string) =>
    dispatch(notificationTemplateActions.getNotificationTemplate(notification_template_id)),
  getBakedBreads: (shop_id: string) => dispatch(bakedBreadActions.getBakedBreads(shop_id)),
  getBakedBreadTags: () => dispatch(bakedBreadTagActions.getBakedBreadTags()),
  initNotification: () => dispatch(notificationActions.initNotification()),
  initNotificationTemplate: () => dispatch(notificationTemplateActions.initNotificationTemplate()),
  validate: (payload: NotificationEntity) =>
    dispatch(notificationActions.validateNotification(payload)),
  edit: (payload: NotificationEntity) => dispatch(notificationActions.editNotification(payload)),
  create: (payload: NotificationEntity) =>
    dispatch(notificationActions.createNotification(payload)),
});
export default connect(mapStateToProps, mapDispatchToProps)(NotificationCreatePage);
